<script setup>
// Google Ads > Genel Bakis
//
// Ust sirada donemin toplamlari, altta en cok harcayan bes kampanya.
// "Ne kadar harcadim, karsiliginda ne aldim" sorusu tek ekranda.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import GoogleAdsKabuk from './GoogleAdsKabuk.vue';
import GoogleAdsTablo from './GoogleAdsTablo.vue';
import { getir, hataMetni, para, sayi } from './kopru.js';

const { t } = useI18n();
const route = useRoute();

const ozet = ref(null);
const kampanyalar = ref([]);
const paraBirimi = ref('');
const yukleniyor = ref(false);
const hata = ref('');

const SUTUNLAR = [
  { alan: 'ad', anahtar: 'GOOGLE_ADS.SUTUN.AD', vurgu: true },
  { alan: 'durum', anahtar: 'GOOGLE_ADS.SUTUN.DURUM', tur: 'durum' },
  { alan: 'harcama', anahtar: 'GOOGLE_ADS.SUTUN.HARCAMA', tur: 'para' },
  { alan: 'tiklama', anahtar: 'GOOGLE_ADS.SUTUN.TIKLAMA', tur: 'sayi' },
  { alan: 'donusum', anahtar: 'GOOGLE_ADS.SUTUN.DONUSUM', tur: 'sayi' },
];

// Tiklama basina maliyet ve donusum basina maliyet sunucudan gelmiyor;
// toplamlardan turetiyoruz. Google'in ortalamasi donem icindeki her gunun
// ortalamasi olurdu; bizim istedigimiz donemin tamaminin orani.
const tiklamaBasi = computed(() => {
  const o = ozet.value;
  if (!o || !o.tiklama) return null;
  return o.harcama / o.tiklama;
});

const donusumBasi = computed(() => {
  const o = ozet.value;
  if (!o || !o.donusum) return null;
  return o.harcama / o.donusum;
});

const KARTLAR = computed(() => {
  const o = ozet.value || {};
  return [
    {
      anahtar: 'GOOGLE_ADS.OZET.HARCAMA',
      deger: para(o.harcama, paraBirimi.value),
      simge: 'i-lucide-wallet',
      renk: 'text-n-amber-11',
    },
    {
      anahtar: 'GOOGLE_ADS.OZET.GOSTERIM',
      deger: sayi(o.gosterim),
      simge: 'i-lucide-eye',
      renk: 'text-n-blue-11',
    },
    {
      anahtar: 'GOOGLE_ADS.OZET.TIKLAMA',
      deger: sayi(o.tiklama),
      simge: 'i-lucide-mouse-pointer-click',
      renk: 'text-n-teal-11',
    },
    {
      anahtar: 'GOOGLE_ADS.OZET.DONUSUM',
      deger: sayi(Math.round((o.donusum || 0) * 10) / 10),
      simge: 'i-lucide-target',
      renk: 'text-n-ruby-11',
    },
    {
      anahtar: 'GOOGLE_ADS.OZET.TIKLAMA_BASI',
      deger: tiklamaBasi.value === null ? '—' : para(tiklamaBasi.value, paraBirimi.value),
      simge: 'i-lucide-divide',
      renk: 'text-n-slate-11',
    },
    {
      anahtar: 'GOOGLE_ADS.OZET.DONUSUM_BASI',
      deger: donusumBasi.value === null ? '—' : para(donusumBasi.value, paraBirimi.value),
      simge: 'i-lucide-calculator',
      renk: 'text-n-slate-11',
    },
  ];
});

const yukle = async aralik => {
  yukleniyor.value = true;
  hata.value = '';
  try {
    const veri = await getir(route.params.accountId, 'genel-bakis', { aralik });
    ozet.value = veri.ozet;
    kampanyalar.value = veri.en_cok_harcayan || [];
    paraBirimi.value = veri.para_birimi || '';
  } catch (e) {
    ozet.value = null;
    kampanyalar.value = [];
    hata.value = hataMetni(e, t('GOOGLE_ADS.HATA'));
  } finally {
    yukleniyor.value = false;
  }
};
</script>

<template>
  <GoogleAdsKabuk :baslik="t('GOOGLE_ADS.GENEL_BAKIS')" @aralik="yukle">
    <div class="flex flex-col gap-5">
      <p v-if="hata" class="text-sm text-n-ruby-11">{{ hata }}</p>

      <div v-if="yukleniyor" class="flex justify-center p-10">
        <Spinner class="text-n-brand" />
      </div>

      <template v-else>
        <div class="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <div
            v-for="kart in KARTLAR"
            :key="kart.anahtar"
            class="flex flex-col gap-1 p-4 border rounded-xl border-n-weak bg-n-solid-1"
          >
            <span class="flex items-center gap-1.5 text-xs text-n-slate-11">
              <span :class="[kart.simge, kart.renk, 'size-3.5']" />
              {{ t(kart.anahtar) }}
            </span>
            <span class="text-lg font-medium text-n-slate-12">
              {{ kart.deger }}
            </span>
          </div>
        </div>

        <section class="flex flex-col gap-2">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('GOOGLE_ADS.EN_COK_HARCAYAN') }}
          </h2>
          <GoogleAdsTablo
            :sutunlar="SUTUNLAR"
            :satirlar="kampanyalar"
            :para-birimi="paraBirimi"
          />
        </section>
      </template>
    </div>
  </GoogleAdsKabuk>
</template>
